import React from "react"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "src/components/ui/sheet"
import { useToast } from "src/components/ui/toast"
import { ProjectForm } from "./ProjectForm"
import { useCreateProject, useUpdateProject } from "../hooks/useProjects"

export function ProjectDrawer({ open, onClose, project, onSuccess }) {
  const isEdit = !!project
  const { toast } = useToast()

  const createHook = useCreateProject()
  const updateHook = useUpdateProject()
  const isPending = createHook.isPending || updateHook.isPending

  async function handleSubmit(data) {
    const { __images, ...fields } = data
    const images = { files: __images?.newFiles || [], removeIds: __images?.removedIds || [] }

    // Drop blank strings, but always keep booleans (active) and send an explicit
    // null for assigned_membership_id so unassigning works on edit.
    const ALWAYS_SEND = new Set(["active", "assigned_membership_id"])
    const payload = Object.fromEntries(
      Object.entries(fields).filter(
        ([k, v]) => ALWAYS_SEND.has(k) || (v !== "" && v !== null && v !== undefined)
      )
    )

    try {
      if (isEdit) {
        await updateHook.mutate(project.id, payload, images)
        toast({ title: "Project updated", description: payload.name })
      } else {
        await createHook.mutate(payload, images)
        toast({ title: "Project created", description: payload.name })
      }
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : (errors?.message || "Something went wrong")
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex flex-col p-0 sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isEdit ? "Edit Project" : "New Project"}</SheetTitle>
          <SheetDescription>
            {isEdit ? "Update the project details below." : "Fill in the details to create a new project."}
          </SheetDescription>
        </SheetHeader>

        <ProjectForm
          project={project}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isPending={isPending}
        />
      </SheetContent>
    </Sheet>
  )
}
